/**
 * Attributes and the Dofus roulette. Bot sources:
 * `rpg/attributes/math.ts` (cost table, multi-level cost, roll clock),
 * `rpg/attributes/roulette.ts#fillDofus` (play menu: every roll left in the
 * window at once), `rpg/attributes/helpers.ts` (`attributeLevelUp`,
 * `buildFailLine`) and `play-menu.ts` (reset).
 */
import { DOFUS_ROLL } from '../balance.js';
import { dofusTiers } from '../data/index.js';
import { GameError } from '../errors.js';
import type { GameEvent } from '../events.js';
import { randInt, type Rng } from '../rng.js';
import { premiumOf } from '../premium.js';
import type { BotUser } from '../state.js';

export type AttributeKey = 'neutral' | 'strength' | 'intelligence' | 'chance' | 'agility' | 'initiative';
export const ATTRIBUTE_KEYS: readonly AttributeKey[] = ['neutral', 'strength', 'intelligence', 'chance', 'agility', 'initiative'];

export type DofusKey = 'one' | 'two' | 'three' | 'four' | 'five' | 'six';
export type DofusCost = [number, number, number, number, number];

/** `computeNextRollTime`: the next odd hour after the last roll (13:10 → 15:00, 14:10 → 15:00). */
export function nextRollAt(date: number): number {
  const d = new Date(date);
  d.setHours(d.getHours() + (d.getHours() % 2 ? 2 : 1));
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), 0, 0).getTime();
}

/** Rolls per window: `1 + premium.roulette + vote.toutou` (the stored vote bonus, as the bot reads it). */
export function maxRolls(save: BotUser, now: number): number {
  return DOFUS_ROLL.maxRolls + premiumOf(save, now).roulette + (save.vote?.toutou ?? 0);
}

/** Rolls left in the current window. */
export function rollsLeft(save: BotUser, now: number): number {
  const used = now > nextRollAt(save.dofus.date) ? 0 : save.dofus.time;
  return Math.max(0, maxRolls(save, now) - used);
}

const LEVEL_COSTS: DofusCost[] = [
  [1, 0, 0, 0, 0], [2, 0, 0, 0, 0], [4, 0, 0, 0, 0], [8, 0, 0, 0, 0],
  [16, 0, 0, 0, 0], [32, 0, 0, 0, 0], [64, 0, 0, 0, 0], [128, 0, 0, 0, 0],
  [1, 0, 0, 0, 0], [2, 1, 0, 0, 0], [4, 2, 0, 0, 0], [8, 4, 0, 0, 0],
  [16, 8, 0, 0, 0], [32, 16, 0, 0, 0], [64, 32, 0, 0, 0], [128, 64, 0, 0, 0],
  [1, 0, 0, 0, 0], [2, 1, 0, 0, 0], [4, 2, 1, 0, 0], [8, 4, 2, 0, 0],
  [16, 8, 4, 0, 0], [32, 16, 8, 0, 0], [64, 32, 16, 0, 0], [128, 64, 32, 0, 0],
  [1, 0, 0, 0, 0], [2, 1, 0, 0, 0], [4, 2, 1, 0, 0], [8, 4, 2, 1, 0],
  [16, 8, 4, 2, 0], [32, 16, 8, 4, 0], [64, 32, 16, 8, 0], [128, 64, 32, 16, 0],
  [1, 0, 0, 0, 0], [2, 1, 0, 0, 0], [4, 2, 1, 0, 0], [8, 4, 2, 1, 0],
  [16, 8, 4, 2, 1], [32, 16, 8, 4, 2], [64, 32, 16, 8, 4], [128, 64, 32, 16, 8],
];

/** `getNextLevel`: Dofus cost of the level after `level` (the table cycles every 40 levels). */
export function attributeLevelCost(level: number): DofusCost {
  return [...(LEVEL_COSTS[level % LEVEL_COSTS.length] ?? [0, 0, 0, 0, 0])] as DofusCost;
}

function owned(save: BotUser): DofusCost {
  return [save.dofus.one, save.dofus.two, save.dofus.three, save.dofus.four, save.dofus.five];
}

function add(a: DofusCost, b: DofusCost): DofusCost {
  return a.map((v, i) => v + (b[i] ?? 0)) as DofusCost;
}

/** `getNumNextLevel`: cost of `amount` levels, or (amount 0) of as many as the Dofus pay for. */
export function attributeUpgradeCost(save: BotUser, level: number, amount: number): { num: number; cost: DofusCost } {
  let cost: DofusCost = [0, 0, 0, 0, 0];
  let num = amount;
  if (num === 0) {
    const have = owned(save);
    let current = attributeLevelCost(level);
    let count = 0;
    const canAfford = () => cost.every((c, i) => c + (current[i] ?? 0) <= (have[i] ?? 0));
    for (let i = 1; i <= DOFUS_ROLL.maxUpgrade && canAfford(); i++) {
      cost = add(cost, current);
      current = attributeLevelCost(level + i);
      count = i;
    }
    num = count;
  } else {
    for (let i = 0; i < num; i++) cost = add(cost, attributeLevelCost(level + i));
  }
  if (num === 0) return { num: 1, cost: attributeLevelCost(level) };
  return { num, cost };
}

/** Levels the current Dofus can buy right now (0 if not even one). */
export function affordableLevels(save: BotUser, key: AttributeKey): number {
  if (key === 'initiative') return save.dofus.six;
  const r = attributeUpgradeCost(save, save.attributes[key], 0);
  const have = owned(save);
  return r.cost.every((c, i) => c <= (have[i] ?? 0)) ? r.num : 0;
}

/** `attributeLevelUp`: `amount` levels (0 = max). Initiative costs one Ébène per level. */
export function upgradeAttribute(save: BotUser, key: AttributeKey, amount: number, events: GameEvent[]): void {
  if (!ATTRIBUTE_KEYS.includes(key) || amount < 0 || !Number.isFinite(amount)) throw new GameError('invalidAmount', { amount });
  if (key === 'initiative') {
    const n = amount === 0 ? save.dofus.six : amount;
    if (n < 1 || save.dofus.six < n) throw new GameError('notEnoughDofus', {});
    save.dofus.six -= n;
    save.attributes.initiative += n;
    events.push({ type: 'attributeUpgraded', attribute: key, levels: n, to: save.attributes.initiative });
    return;
  }
  const level = save.attributes[key];
  const { num, cost } = attributeUpgradeCost(save, level, amount);
  const have = owned(save);
  if (!cost.every((c, i) => c <= (have[i] ?? 0))) throw new GameError('notEnoughDofus', {});
  save.dofus.one -= cost[0];
  save.dofus.two -= cost[1];
  save.dofus.three -= cost[2];
  save.dofus.four -= cost[3];
  save.dofus.five -= cost[4];
  save.attributes[key] += num;
  events.push({ type: 'attributeUpgraded', attribute: key, levels: num, to: save.attributes[key] });
}

/**
 * Orbs the reset screen offers (`fillAttributeReset`): on a Monday the screen
 * adds `token.amount.refresh` to what is stored, so a Monday reset costs
 * nothing stored (bot behavior until the shared fix P-17 lands in T4.9).
 */
export function resetOrbs(save: BotUser, now: number): number {
  const amount = save.attributes.token.amount;
  return amount.available + (new Date(now).getDay() === 1 ? amount.refresh : 0);
}

/** Reset (`fillAttributeResetConfirm`): one orb; Dofus back to everything ever rolled, elements to 0 (not initiative). */
export function resetAttributes(save: BotUser, now: number, events: GameEvent[]): void {
  const token = save.attributes.token;
  const available = resetOrbs(save, now);
  if (available < DOFUS_ROLL.resetCost) throw new GameError('notEnoughOrbs', { have: available, needed: DOFUS_ROLL.resetCost });
  token.amount.available = available - DOFUS_ROLL.resetCost;
  token.useDate = now;
  const s = save.stats.dofus;
  save.dofus.one = s.one;
  save.dofus.two = s.two;
  save.dofus.three = s.three;
  save.dofus.four = s.four;
  save.dofus.five = s.five;
  for (const k of ['agility', 'chance', 'intelligence', 'strength', 'neutral'] as const) save.attributes[k] = 0;
  events.push({ type: 'attributesReset' });
}

/** `utils/math.ts#randDistinctInt` (deterministic fill if the RNG keeps repeating). */
function randDistinct(rng: Rng, max: number, n: number): number[] {
  const out: number[] = [];
  if (max < n) {
    for (let i = 0; i < n; i++) out.push(randInt(rng, 0, max));
    return out;
  }
  for (let tries = 0; out.length < n && tries < 1000; tries++) {
    const v = randInt(rng, 0, max);
    if (!out.includes(v)) out.push(v);
  }
  for (let v = 0; out.length < n; v++) if (!out.includes(v)) out.push(v);
  return out;
}

export interface ReelLine {
  tier: DofusKey;
  /** Three reel symbols (emoji tokens); identical on a hit. */
  symbols: [string, string, string];
  hit: boolean;
  multiplier: number;
}

/** `buildFailLine`: three "miss" symbols (consumes the same rolls as the bot). */
function failLine(tier: DofusKey, rng: Rng): ReelLine {
  const def = dofusTiers.find((t) => t.key === tier);
  const no = def?.no ?? [];
  if (no.length === 0) return { tier, symbols: ['', '', ''], hit: false, multiplier: 0 };
  const [i0 = 0, i1 = 0] = randDistinct(rng, no.length, 2);
  const i2 = randInt(rng, 0, no.length);
  return { tier, symbols: [no[i0] ?? '', no[i2] ?? '', no[i1] ?? ''], hit: false, multiplier: 0 };
}

function hitLine(tier: DofusKey, multiplier: number): ReelLine {
  const e = dofusTiers.find((t) => t.key === tier)?.emoji ?? '';
  return { tier, symbols: [e, e, e], hit: true, multiplier };
}

/** One spin (`fillDofus` loop body). */
function spin(save: BotUser, now: number, rng: Rng, events: GameEvent[]): void {
  const first = save.dofus.time === 0;
  const range = first ? DOFUS_ROLL.first : DOFUS_ROLL.next;
  const roll = () => randInt(rng, range.min, range.max);
  const earned: Record<DofusKey, number> = { one: 0, two: 0, three: 0, four: 0, five: 0, six: 0 };
  const lines: ReelLine[] = [];
  let money = 0;

  let multi = roll();
  earned.one = multi;
  money += DOFUS_ROLL.money.one * multi;
  lines.push(hitLine('one', multi));

  const six = randInt(rng, 0, DOFUS_ROLL.sixRate) === 0 && first;
  for (const tier of ['two', 'three', 'four', 'five'] as const) {
    if (randInt(rng, 0, DOFUS_ROLL.thresholds[tier]) === 0 || six) {
      multi = roll();
      earned[tier] = multi;
      money += DOFUS_ROLL.money[tier] * multi;
      lines.push(hitLine(tier, multi));
    } else {
      lines.push(failLine(tier, rng));
    }
  }
  if (six) {
    earned.six = 1;
    money += DOFUS_ROLL.money.six;
    lines.push(hitLine('six', 1));
  }

  for (const k of Object.keys(earned) as DofusKey[]) {
    save.dofus[k] += earned[k];
    save.stats.dofus[k] += earned[k];
  }
  save.dofus.time += 1;
  save.dofus.date = now;
  save.stats.dofus.time += 1;
  money = Math.floor(money);
  save.money += money;
  save.stats.money.total += money;
  events.push({ type: 'dofusRolled', earned, money, lines, first });
}

/** The 2-hour Dofus roulette from the play menu (`fillDofus`): every roll left in the window, one event each. */
export function dofusRoll(save: BotUser, now: number, rng: Rng, events: GameEvent[]): void {
  const canRoll = nextRollAt(save.dofus.date);
  if (now > canRoll) save.dofus.time = 0;
  const max = maxRolls(save, now);
  if (now < canRoll && save.dofus.time >= max) throw new GameError('dofusCooldown', { at: canRoll });
  while (save.dofus.time < max) spin(save, now, rng, events);
}
